import { useLoginInfo } from 'app/_hooks'
import type { AxiosResponse } from 'axios'
import { useCallback } from 'react'
import { useAddLocaleParams } from '~/features/i18n/hooks/useAddLocaleParams'
import { useAxios } from '~/libs/axios'
import { Language } from '~/types/api/users'
import {
  createNewMyProfileFormData,
  createUpdateMyProfileFormData,
} from './createUpdateMyProfileFormData'
import {
  NewMyProfileArgs,
  NewMyProfileResponse,
  UpdateAutoPlayArgs,
  UpdateAutoPlayResponse,
  UpdateMyLanguageArgs,
  UpdateMyLanguageResponse,
  UpdateMyProfileArgs,
  UpdateMyProfileResponse,
} from './type'

interface MyProfileApi {
  newMyProfile: (args: NewMyProfileArgs) => Promise<void>
  updateMyLanguage: (language: Language) => Promise<void>
  updateAutoPlay: (autoplay: boolean) => Promise<void>
  updateMyProfile: (args: UpdateMyProfileArgs) => Promise<void>
}

export function useMyProfileApi(): MyProfileApi {
  const axios = useAxios()
  const localeParams = useAddLocaleParams()
  const loginInfo = useLoginInfo()

  const newMyProfile = useCallback(
    async (args: NewMyProfileArgs) => {
      const formData = createNewMyProfileFormData(args)
      await axios.post<NewMyProfileResponse, AxiosResponse<NewMyProfileResponse>, FormData>(
        `/v2/user?${localeParams}`,
        formData,
        { headers: { 'content-type': 'multipart/form-data' } },
      )
      window.location.reload()
    },
    [axios, localeParams],
  )

  const updateMyProfile = useCallback(
    async (args: UpdateMyProfileArgs) => {
      const formData = createUpdateMyProfileFormData(args)
      await axios.patch<UpdateMyProfileResponse, AxiosResponse<UpdateMyProfileResponse>, FormData>(
        `/v2/user?${localeParams}`,
        formData,
        { headers: { 'content-type': 'multipart/form-data' } },
      )
    },
    [axios, localeParams],
  )

  const updateMyLanguage = useCallback(
    async (language: Language) => {
      await axios.patch<
        UpdateMyLanguageResponse,
        AxiosResponse<UpdateMyLanguageResponse>,
        UpdateMyLanguageArgs
      >(`/v2/user?${localeParams}`, { language })
    },
    [axios, localeParams],
  )

  const updateAutoPlay = useCallback(
    async (autoplay: boolean) => {
      if (loginInfo.status === 'signedIn') {
        await axios.patch<
          UpdateAutoPlayResponse,
          AxiosResponse<UpdateAutoPlayResponse>,
          UpdateAutoPlayArgs
        >(`/v2/user?${localeParams}`, { autoplay_videos: autoplay })
      }
    },
    [axios, localeParams, loginInfo],
  )

  return { newMyProfile, updateMyLanguage, updateAutoPlay, updateMyProfile }
}
